import React, {useState, useCallback} from 'react';
import {Button, Modal} from "react-bootstrap";
import http from "../../config/http-common";
import {useNavigate} from "react-router-dom";
import ProductModalV1 from "./ProductModalV1";

type Product = {
    ID: string,
    CategoryID: string,
    Name: string,
    SKU: string,
    ImageLink: string,
    Units: Unit[],
};

type Unit = {
    Id: string,
    Name: string,
    Price: number,
};

interface ModalProps {
    product: Product;
    categoryUnit: string;
    handleCategoryClick: (categoryId: string) => void;
    refreshProductsForCategory?: (categoryId: string) => void;
    refreshPage?: () => void;
}

const ProductComponent: React.FC<ModalProps> = ({product, categoryUnit, handleCategoryClick, refreshProductsForCategory, refreshPage}) => {
    const navigate = useNavigate();
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [showEditProductModal, setShowEditProductModal] = useState(false);

    const renderKey = useCallback(() => Math.random().toString(), []);

    const handleCancelDelete = () => {
        setShowDeleteConfirmationModal(false);
    };

    const handleShowEditProduct = () => {
        setShowEditProductModal(true);
    };

    const handleCloseEditProduct = () => {
        setShowEditProductModal(false);
        refreshPage?.();
    };

    const askDeleteConfirmation = () => {
        setShowDeleteConfirmationModal(true);
    };

    const handleConfirmDelete = () => {
        handleProductDelete(product?.ID);
        setShowDeleteConfirmationModal(false);
    };

    const handleProductDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await http.delete('/api/product', {
                headers: {
                    'Token': token
                },
                params: {productID: id}
            });
            if (product) {
                if (refreshProductsForCategory) {
                    refreshProductsForCategory(product.CategoryID);
                } else {
                    handleCategoryClick(product.CategoryID);
                }
            }
        } catch (error) {
            navigate('/login');
        }
    };

    return (
        <div className="col-12 mb-2">
            <div className="row align-items-center p-2 border rounded-3 shadow-sm g-0">
                <div className="col-3 col-sm-2 col-md-1 text-center">
                    <img className="img-fluid rounded" style={{maxHeight: '90px', objectFit: 'cover'}}
                         src={product?.ImageLink || 'https://via.placeholder.com/150?text=No+image'} alt="Produkts"/>
                </div>
                <div className="col-9 col-sm-10 col-md-5 col-xl-6 ps-3">
                    <div className="bg-light rounded-2 px-2 py-1 mb-1 d-inline-block w-100">
                        <h5 className="mb-0 text-truncate" title={product?.Name}>{product?.Name}</h5>
                    </div>
                    <small className="text-muted">SKU: {product?.SKU}</small>
                </div>
                <div className="col-12 col-md-4 col-xl-4 mt-2 mt-md-0">
                    <div className="d-flex flex-wrap gap-2">
                        {
                            product?.Units?.map((unit: Unit) => (
                                <span key={unit.Id} className="badge bg-secondary-subtle text-dark border border-secondary-subtle rounded-pill px-3 py-2">
                                    {unit.Name} {categoryUnit}. — <strong className="ms-1">{unit.Price} €</strong>
                                </span>
                            ))
                        }
                    </div>
                </div>
                <div className="col-12 col-md-2 col-xl-1 text-end mt-2 mt-md-0">
                    <button aria-label="Rediģēt" className="btn btn-outline-secondary btn-sm me-1" onClick={handleShowEditProduct}>
                        <i className="bi bi-pencil"></i>
                    </button>
                    <button aria-label="Dzēst" className="btn btn-outline-danger btn-sm" onClick={askDeleteConfirmation}>
                        <i className="bi bi-x-lg"></i>
                    </button>

                    <Modal show={showDeleteConfirmationModal} onHide={handleCancelDelete}>
                        <Modal.Header closeButton>
                            <Modal.Title>Dzēšanas apstiprinājums</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Vai vēlaties dzēst izvēlēto produktu?</Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCancelDelete}>Nē</Button>
                            <Button variant="primary" onClick={handleConfirmDelete}>Jā</Button>
                        </Modal.Footer>
                    </Modal>

                    <ProductModalV1
                        productToEdit={product || null}
                        editMode={true}
                        show={showEditProductModal}
                        key={renderKey()}
                        selectedCategoryUnit={categoryUnit || ''}
                        selectedCategoryId={product.CategoryID || ''}
                        closeHandler={handleCloseEditProduct}
                        reloadCatalog={() => (refreshProductsForCategory ? refreshProductsForCategory(product.CategoryID || '') : handleCategoryClick(product.CategoryID || ''))}/>
                </div>
            </div>
        </div>
    );
}

export default ProductComponent;